import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../../../../generic-components/Button';
import { useSetSumAutoSavingsQuery } from '../../../../hooks/query/useSetSumAutoSavingsQuery';
import { useStore } from '../../../../store';
import { useToggleAutoSavingsQuery } from '../../../../hooks/query/useToggleAutoSavingsQuery';
import { MAX_AUTO_SAVING_ALLOWED_DIGITS, SET_UP_AUTO_SAVING, START_STEPS } from '../../constants';
import { currencyInputValueToNumber, handleInputWithCurrencyOnChange } from '../../../../helpers/currency.helper';
import { CURRENCY_USD_SIGN, FEATURE_FLAG, ENTER_KEY } from '../../../../constants/general';
import InputWithCurrency from '../../../../generic-components/InputWithCurrency';
import { validateFeatureFlag } from '../../../../services/featureFlagService';
import { useCustomerHelper } from '../../../../hooks/utilities/useCustomerHelper';

const SetUpAutoSaving = () => {
  const navigate = useNavigate();
  const { termsAndConditionsLink, termsAndConditionsLinkTitle } = useCustomerHelper();
  const navigateWIthParams = (
    isSavingsActivated: string,
    isSuccess: boolean,
    activation?: boolean,
    dailySave?: number,
  ) => {
    if (activation !== undefined && dailySave !== undefined) {
      navigate(`/emergency-bucket-dashboard/:${isSavingsActivated}-${isSuccess}-${activation}-${dailySave}`);
    } else {
      navigate(`/emergency-bucket-dashboard/:${isSavingsActivated}-${isSuccess}`);
    }
  };
  const {
    MAX_DAILY_SAVING,
    MIN_DAILY_SAVING,
    TITLE,
    TEXT,
    INPUT,
    VALUE_TO_CONFIRM,
    A_DAY,
    ERROR_MESSAGE,
    BUTTON,
    DISCLAIMER,
    TERMS_CONDITIONS,
  } = SET_UP_AUTO_SAVING;
  const { STEP_1, STEP_2 } = START_STEPS;
  const summary = useStore((state) => state.summary);
  const { customerId } = useStore((state) => state.currUser());
  const INPUT_ZERO_STATE = '';
  const NULL_INPUT_VALUE = '0';
  const NULL_DOT_INPUT_VALUE = '0.';
  const initialInputValue =
    summary.emergencyBucketResponse.autoSave && summary.emergencyBucketResponse.amount
      ? `${summary.emergencyBucketResponse.amount}`
      : `${INPUT_ZERO_STATE}`;
  const [inputValue, setInputValue] = useState<string>(`${initialInputValue}`);
  const [currentStep, setCurrentStep] = useState(STEP_1);
  const { refetch: activateSavings } = useToggleAutoSavingsQuery({
    customerId,
    isAutoSaving: true,
    onSuccess: () => {
      const isActivated: boolean = summary.emergencyBucketResponse.active;
      navigateWIthParams('isSavingsActivated', true, !isActivated, parseFloat(inputValue));
    },
    onFailure: () => {
      navigateWIthParams('isSavingsActivated', false);
    },
  });
  const bucketActivation = () => {
    if (summary && summary.emergencyBucketResponse) {
      return !summary.emergencyBucketResponse.active;
    }
    return true;
  };
  const {
    refetch: setUpAmount,
    isRefetching,
    isLoading,
  } = useSetSumAutoSavingsQuery({
    customerId,
    amount: parseFloat(inputValue.replaceAll(',', '')),
    onSuccess: () => {
      activateSavings();
    },
  });
  const isAmountIllegal =
    inputValue === INPUT_ZERO_STATE ||
    currencyInputValueToNumber(inputValue) < MIN_DAILY_SAVING ||
    currencyInputValueToNumber(inputValue) > MAX_DAILY_SAVING ||
    inputValue === NULL_INPUT_VALUE ||
    inputValue === NULL_DOT_INPUT_VALUE;

  const handleInputChange = (event: { target: { value: string } }) => {
    if (event.target.value.length > MAX_AUTO_SAVING_ALLOWED_DIGITS) return;
    handleInputWithCurrencyOnChange(event, setInputValue, true);
  };

  const { t } = useTranslation();

  const inputRef = useRef<HTMLInputElement>();

  useEffect(() => {
    if (inputRef.current && currentStep === STEP_1) inputRef.current?.focus();
  });

  useEffect(() => {
    if (currentStep === STEP_2 && inputValue.indexOf('.') === inputValue.length - 1) {
      setInputValue(inputValue.replaceAll('.', ''));
    }
  }, [currentStep]);

  return (
    <div className='px-10 pt-[3.125rem] pb-[0.75rem] bg-bw00 rounded-lg flex flex-col items-center h-[27.313rem]'>
      {currentStep === STEP_1 && (
        <>
          <span className={`${TITLE.CLASS_NAME} h-[1.5rem]`}>{t(TITLE.STEP_1)}</span>
          <span className={`${TEXT.CLASS_NAME_STEP_1} h-[1rem]`}>{t(TEXT.STEP_1)}</span>
          <InputWithCurrency
            containerClassName=''
            currency={CURRENCY_USD_SIGN}
            currencyClassName='font-metricMedium text-base text-bw07'
            inputRef={inputRef as React.Ref<HTMLInputElement>}
            id='setUpAutoSaving'
            inputCLassName={`caret-action03 ${INPUT.CLASS_NAME} ${isAmountIllegal && inputValue !== INPUT_ZERO_STATE ? 'border-red02' : 'border-action03'}`}
            onChange={handleInputChange}
            value={inputValue}
            eventKey={ENTER_KEY}
            handleKeyDown={() => {
              if (!isAmountIllegal) setCurrentStep(STEP_2);
            }}
          />
          <div
            className={`${A_DAY.CLASS_NAME_STEP_1} ${parseFloat(inputValue.replaceAll(',', '')) > MAX_DAILY_SAVING ? 'mb-1' : 'mb-[4.125rem]'}`}
          >
            {t(A_DAY.PLACE_HOLDER)}
          </div>
          {currencyInputValueToNumber(inputValue) > MAX_DAILY_SAVING && (
            <span className={ERROR_MESSAGE.CLASS_NAME}>{t(ERROR_MESSAGE.MESSAGE)}</span>
          )}
          <Button
            id='setUpAmountOrActivateSavingsStep1'
            onClick={() => {
              setCurrentStep(STEP_2);
            }}
            disable={isAmountIllegal}
            className={`${BUTTON.CLASS_NAME} ${isAmountIllegal ? 'bg-action02 text-opacity-50' : ' bg-action03 hover:bg-action04'}`}
            title={t(BUTTON.TITLE.STEP_1)}
          />
          <span
            className={`${DISCLAIMER.CLASS_NAME} h-[2.25rem] mt-[0.75rem] ${validateFeatureFlag(FEATURE_FLAG.EMERGENCY_SAVING_ACCOUNT) ? 'mx-6' : 'mx-1'}`}
          >
            {validateFeatureFlag(FEATURE_FLAG.EMERGENCY_SAVING_ACCOUNT)
              ? t(DISCLAIMER.PLACE_HOLDER.PRO)
              : t(DISCLAIMER.PLACE_HOLDER.BASIC)}
          </span>
        </>
      )}
      {currentStep === STEP_2 && (
        <>
          <span className={`${TITLE.CLASS_NAME} h-[1.5rem] leading-[1.5rem]`}>{t(TITLE.STEP_2)}</span>
          <span className={`${TEXT.CLASS_NAME_STEP_2} h-[2.125rem]`}>{t(TEXT.STEP_2)}</span>
          <span className={`${VALUE_TO_CONFIRM.CLASS_NAME} h-[3.25rem]`}>{`$${inputValue.replaceAll(' ', '')}`}</span>
          <span className={`${A_DAY.CLASS_NAME_STEP_2} h-[1.5rem] mb-[5.5rem]`}>{t(A_DAY.PLACE_HOLDER)}</span>
          <Button
            id='setUpAmountOrActivateSavingsStep2'
            isLoading={isRefetching || isLoading}
            onClick={setUpAmount}
            className={`${BUTTON.CLASS_NAME} mb-[0.75rem] bg-action03 hover:bg-action04`}
            title={t(BUTTON.TITLE.STEP_2)}
          />
          {!validateFeatureFlag(FEATURE_FLAG.EMERGENCY_SAVING_ACCOUNT) || !bucketActivation() ? (
            <span
              className={`${DISCLAIMER.CLASS_NAME} h-[2.25rem] ${validateFeatureFlag(FEATURE_FLAG.EMERGENCY_SAVING_ACCOUNT) ? 'mx-6' : 'mx-1'}`}
            >
              {validateFeatureFlag(FEATURE_FLAG.EMERGENCY_SAVING_ACCOUNT)
                ? t(DISCLAIMER.PLACE_HOLDER.PRO)
                : t(DISCLAIMER.PLACE_HOLDER.BASIC)}
            </span>
          ) : (
            <Button
              onClick={() => {
                window.open(termsAndConditionsLink, '_blank');
              }}
              className={TERMS_CONDITIONS.CLASS_NAME}
              id='termsAndConditionsButton'
              childElement={<span>{termsAndConditionsLinkTitle}</span>}
            />
          )}
        </>
      )}
    </div>
  );
};

export default SetUpAutoSaving;
